import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  MessageCircle, QrCode, Clock, CheckCircle, AlertTriangle,
  RefreshCw, Wifi, Send, Loader2, Inbox, XCircle,
  Megaphone, FileText, ChevronDown, ChevronUp, RotateCcw, Store,
  Trash2, RotateCw, Archive, MapPin, Upload, X, Paperclip, Users,
  Filter, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAuthAxios } from '@/store';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR') + ' ' + new Date(d).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }); } catch { return '-'; }
};

const msgTypeLabel = (type) => {
  const labels = {
    admin_test: 'Test', broadcast: 'Duyuru',
    auto_welcome: 'Hos Geldiniz', auto_trial_start: 'Deneme',
    payment_created: 'Odeme Sozu', reminder: 'Vade Hatirlatma',
    payment_reminder: 'Odeme Hatirlatma', due_date_reminder: 'Vade Hatirlatma',
    auto_overdue_reminder: 'Vadesi Gecmis', auto_approaching_reminder: 'Yaklasiyor',
    auto_due_date_reminder: 'Vade Gunu', order_confirmation: 'Siparis Onayi',
    overdue: 'Vadesi Gecmis',
  };
  return labels[type] || type;
};

const ALLOWED_TEMPLATES = ['payment_created', 'reminder', 'overdue', 'order_confirmation'];

const TURKISH_CITIES = [
  'Adana','Adiyaman','Afyonkarahisar','Agri','Aksaray','Amasya','Ankara','Antalya','Ardahan','Artvin',
  'Aydin','Balikesir','Bartin','Batman','Bayburt','Bilecik','Bingol','Bitlis','Bolu','Burdur',
  'Bursa','Canakkale','Cankiri','Corum','Denizli','Diyarbakir','Duzce','Edirne','Elazig','Erzincan',
  'Erzurum','Eskisehir','Gaziantep','Giresun','Gumushane','Hakkari','Hatay','Igdir','Isparta','Istanbul',
  'Izmir','Kahramanmaras','Karabuk','Karaman','Kars','Kastamonu','Kayseri','Kilis','Kirikkale','Kirklareli',
  'Kirsehir','Kocaeli','Konya','Kutahya','Malatya','Manisa','Mardin','Mersin','Mugla','Mus',
  'Nevsehir','Nigde','Ordu','Osmaniye','Rize','Sakarya','Samsun','Sanliurfa','Siirt','Sinop',
  'Sirnak','Sivas','Tekirdag','Tokat','Trabzon','Tunceli','Usak','Van','Yalova','Yozgat','Zonguldak'
];

export default function AdminWhatsAppPage() {
  const [status, setStatus] = useState('disconnected');
  const [qrCode, setQrCode] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);

  const [sendForm, setSendForm] = useState({ phone: '', message: '' });
  const [isSending, setIsSending] = useState(false);

  // Broadcast state
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastMode, setBroadcastMode] = useState('all'); // all, city, custom
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedDealerIds, setSelectedDealerIds] = useState([]);
  const [allDealers, setAllDealers] = useState([]);
  const [cityStats, setCityStats] = useState([]);
  const [totalDealers, setTotalDealers] = useState(0);
  const [withPhone, setWithPhone] = useState(0);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [dealerSearch, setDealerSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [deletedMessages, setDeletedMessages] = useState([]);
  const [msgStats, setMsgStats] = useState({ total_sent: 0, total_failed: 0, total_messages: 0, today_sent: 0, today_failed: 0, pending_in_queue: 0 });
  const [msgTab, setMsgTab] = useState('active');

  const [templates, setTemplates] = useState([]);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(null);

  // Message queue filter (clickable stats)
  const [msgFilter, setMsgFilter] = useState('all'); // all | pending | sent | failed

  const fetchStatus = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/whatsapp/status');
      setStatus(res.data.status);
      setQrCode(res.data.qr);
      if (res.data.status === 'connected' && pollInterval) { clearInterval(pollInterval); setPollInterval(null); }
    } catch (e) { console.error(e); }
  }, [pollInterval]);

  const fetchAll = useCallback(async (statusFilter = msgFilter) => {
    try {
      const api = getAuthAxios();
      const msgParams = statusFilter && statusFilter !== 'all'
        ? `/admin/whatsapp/messages?limit=200&status=${statusFilter}`
        : `/admin/whatsapp/messages?limit=200`;
      const [mRes, stRes, tgRes, tmRes] = await Promise.all([
        api.get(msgParams),
        api.get('/admin/whatsapp/message-stats'),
        api.get('/admin/whatsapp/broadcast-targets'),
        api.get('/admin/whatsapp/templates'),
      ]);
      setMessages(mRes.data);
      setMsgStats(stRes.data);
      setTemplates(tmRes.data);
      // Broadcast targets
      setTotalDealers(tgRes.data.all || 0);
      setWithPhone(tgRes.data.with_phone || 0);
      setCityStats(tgRes.data.cities || []);
      setAllDealers(tgRes.data.dealers || []);
    } catch (e) { console.error(e); }
  }, [msgFilter]);

  const fetchDeleted = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/whatsapp/messages/deleted');
      setDeletedMessages(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchAll();
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, []);

  // Filtre değiştiğinde sunucudan ilgili statüdeki mesajları yeniden çek
  useEffect(() => {
    fetchAll(msgFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgFilter]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const api = getAuthAxios();
      const res = await api.post('/admin/whatsapp/connect');
      if (res.data.status === 'connected') { setStatus('connected'); setQrCode(null); toast.success('WhatsApp baglandi!'); }
      else if (res.data.qr) {
        setQrCode(res.data.qr); setStatus('waiting_for_scan');
        toast.info('QR kodu telefonunuzla tarayin');
        setPollInterval(setInterval(fetchStatus, 3000));
      } else {
        toast.info('QR kod olusturuluyor...');
        setTimeout(async () => {
          const retry = await api.get('/admin/whatsapp/status');
          if (retry.data.qr) { setQrCode(retry.data.qr); setStatus('waiting_for_scan'); setPollInterval(setInterval(fetchStatus, 3000)); }
        }, 2000);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Baglanti hatasi'); }
    finally { setIsConnecting(false); }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const api = getAuthAxios();
      await api.post('/admin/whatsapp/disconnect');
      setStatus('disconnected'); setQrCode(null);
      toast.success('Baglanti kesildi');
      if (pollInterval) { clearInterval(pollInterval); setPollInterval(null); }
    } catch { toast.error('Baglanti kesme hatasi'); }
    finally { setIsDisconnecting(false); }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!sendForm.phone || !sendForm.message) return;
    setIsSending(true);
    try {
      const api = getAuthAxios();
      await api.post('/admin/whatsapp/send-test', sendForm);
      toast.success('Mesaj kuyruga alindi');
      setSendForm({ phone: '', message: '' });
      setTimeout(fetchAll, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gonderme hatasi'); }
    finally { setIsSending(false); }
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 16 * 1024 * 1024) { toast.error('Dosya boyutu 16MB\'dan buyuk olamaz'); return; }
    setIsUploading(true);
    try {
      const api = getAuthAxios();
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/whatsapp/upload-broadcast-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedFile({ url: res.data.url, filename: res.data.filename, size: res.data.size });
      toast.success('Dosya yuklendi');
    } catch (err) { toast.error(err.response?.data?.detail || 'Dosya yuklenemedi'); }
    finally { setIsUploading(false); }
  };

  const removeFile = () => setUploadedFile(null);

  // Broadcast
  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) { toast.error('Mesaj icerigi gerekli'); return; }
    setIsBroadcasting(true);
    try {
      const api = getAuthAxios();
      const payload = { message: broadcastMsg };
      if (broadcastMode === 'city') {
        payload.target = 'city';
        payload.cities = selectedCities;
      } else if (broadcastMode === 'custom') {
        payload.target = 'custom';
        payload.dealer_ids = selectedDealerIds;
      } else {
        payload.target = 'all';
      }
      if (uploadedFile) {
        payload.media_url = uploadedFile.url;
        payload.media_filename = uploadedFile.filename;
      }
      const res = await api.post('/admin/whatsapp/broadcast', payload);
      toast.success(`${res.data.queued} bayiye mesaj kuyruga alindi`);
      setBroadcastMsg('');
      setUploadedFile(null);
      setSelectedCities([]);
      setSelectedDealerIds([]);
      setTimeout(fetchAll, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Duyuru gonderilemedi'); }
    finally { setIsBroadcasting(false); }
  };

  // City toggle
  const toggleCity = (cityName) => {
    setSelectedCities(prev => prev.includes(cityName) ? prev.filter(c => c !== cityName) : [...prev, cityName]);
  };

  // Dealer toggle
  const toggleDealer = (dealerId) => {
    setSelectedDealerIds(prev => prev.includes(dealerId) ? prev.filter(d => d !== dealerId) : [...prev, dealerId]);
  };

  // Select all filtered dealers
  const selectAllFilteredDealers = () => {
    const filtered = filteredDealers.map(d => d.id);
    setSelectedDealerIds(prev => {
      const allSelected = filtered.every(id => prev.includes(id));
      if (allSelected) return prev.filter(id => !filtered.includes(id));
      return [...new Set([...prev, ...filtered])];
    });
  };

  // Computed: target count
  const targetCount = useMemo(() => {
    if (broadcastMode === 'all') return totalDealers;
    if (broadcastMode === 'city') {
      if (selectedCities.length === 0) return 0;
      return allDealers.filter(d => selectedCities.includes(d.city || 'Belirtilmemis')).length;
    }
    if (broadcastMode === 'custom') return selectedDealerIds.length;
    return 0;
  }, [broadcastMode, totalDealers, selectedCities, selectedDealerIds, allDealers]);

  // Filtered dealers for custom mode
  const filteredDealers = useMemo(() => {
    let filtered = allDealers;
    if (dealerSearch) {
      const q = dealerSearch.toLowerCase();
      filtered = filtered.filter(d =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.company_name || '').toLowerCase().includes(q) ||
        (d.city || '').toLowerCase().includes(q) ||
        (d.phone || '').includes(q)
      );
    }
    return filtered;
  }, [allDealers, dealerSearch]);

  // Filtered cities
  const filteredCities = useMemo(() => {
    if (!citySearch) return cityStats;
    const q = citySearch.toLowerCase();
    return cityStats.filter(c => c.name.toLowerCase().includes(q));
  }, [cityStats, citySearch]);

  const handleExpandTemplate = (ttype) => {
    if (expandedTemplate === ttype) { setExpandedTemplate(null); return; }
    setExpandedTemplate(ttype);
    const tmpl = templates.find(t => t.template_type === ttype);
    setEditingContent(tmpl?.content || '');
  };

  const handleSaveTemplate = async (ttype) => {
    setSavingTemplate(ttype);
    try {
      const api = getAuthAxios();
      await api.put(`/admin/whatsapp/templates/${ttype}`, { content: editingContent });
      toast.success('Sablon kaydedildi');
      const res = await api.get('/admin/whatsapp/templates');
      setTemplates(res.data);
      setExpandedTemplate(null);
    } catch { toast.error('Sablon kaydedilemedi'); }
    finally { setSavingTemplate(null); }
  };

  const handleResetTemplate = async (ttype) => {
    setSavingTemplate(ttype);
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/whatsapp/templates/${ttype}`);
      toast.success('Sablon varsayilana sifirlandi');
      const res = await api.get('/admin/whatsapp/templates');
      setTemplates(res.data);
      setExpandedTemplate(null);
    } catch { toast.error('Sablon sifirlanamadi'); }
    finally { setSavingTemplate(null); }
  };

  const handleDeleteMessage = async (msgId) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/whatsapp/messages/${msgId}`);
      toast.success('Mesaj silindi');
      fetchAll();
    } catch { toast.error('Silinemedi'); }
  };

  const handleResendMessage = async (msgId) => {
    try {
      const api = getAuthAxios();
      const res = await api.post(`/admin/whatsapp/messages/${msgId}/resend`);
      toast.success(res.data.message);
      fetchAll();
    } catch { toast.error('Gonderilemedi'); }
  };

  const [retryingAll, setRetryingAll] = useState(false);

  // Mesajlar artık server-side filtreli geliyor; client-side filtre gerekmiyor
  const filteredMessages = messages;

  const failedCount = msgStats.total_failed || 0;

  const handleRetryAllFailed = async () => {
    if (failedCount === 0) return;
    setRetryingAll(true);
    try {
      const api = getAuthAxios();
      let success = 0;
      const failedMsgs = messages.filter(m => m.status === 'failed');
      for (const msg of failedMsgs) {
        try {
          await api.post(`/admin/whatsapp/messages/${msg.id}/resend`);
          success++;
        } catch {}
      }
      toast.success(`${success}/${failedMsgs.length} mesaj yeniden kuyruga eklendi`);
      fetchAll();
    } catch { toast.error('Toplu gonderim basarısız'); }
    finally { setRetryingAll(false); }
  };

  const handleRestoreMessage = async (msgId) => {
    try {
      const api = getAuthAxios();
      await api.put(`/admin/whatsapp/messages/${msgId}/restore`);
      toast.success('Mesaj geri yuklendi');
      fetchDeleted();
      fetchAll();
    } catch { toast.error('Geri yuklenemedi'); }
  };

  const isConnected = status === 'connected';
  const filteredTemplates = templates.filter(t => ALLOWED_TEMPLATES.includes(t.template_type));

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-3 sm:space-y-6" data-testid="admin-whatsapp-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">WhatsApp Yonetimi</h1>
        <p className="text-sm text-gray-500">Baglanti, duyuru ve mesaj sablonlari</p>
      </div>

      {/* Connection + Test */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card data-testid="connection-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              {isConnected ? <Wifi className="h-5 w-5 text-green-500" /> : <QrCode className="h-5 w-5 text-red-500" />}
              WhatsApp Baglantisi
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            {isConnected ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="rounded-full bg-green-100 p-4"><CheckCircle className="h-12 w-12 text-green-500" /></div>
                <p className="text-lg font-medium text-green-600">WhatsApp Bagli</p>
                <p className="text-sm text-gray-500 text-center">Sistem aktif, mesajlar gonderilebilir</p>
                <Button variant="outline" onClick={handleDisconnect} disabled={isDisconnecting} data-testid="disconnect-btn">
                  {isDisconnecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Kesiliyor...</> : 'Baglantiyi Kes'}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                {qrCode ? (
                  <div className="rounded-xl border-4 border-green-200/40 p-3 bg-white">
                    <img src={qrCode} alt="QR Code" className="h-48 w-48 sm:h-52 sm:w-52" data-testid="qr-code-image" />
                  </div>
                ) : (
                  <div className="flex h-48 w-48 items-center justify-center rounded-xl border-4 border-dashed border-gray-200">
                    <p className="text-center text-sm text-gray-400 px-4">QR kod olusturmak icin butona tiklayin</p>
                  </div>
                )}
                {status === 'waiting_for_scan' && (
                  <div className="flex items-center gap-2 text-sm text-gray-500"><RefreshCw className="h-4 w-4 animate-spin" />Tarama bekleniyor...</div>
                )}
                <Button onClick={handleConnect} disabled={isConnecting} className="bg-green-600 hover:bg-green-700" data-testid="connect-btn">
                  {isConnecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Baglaniyor...</> : <><QrCode className="mr-2 h-4 w-4" />QR Kod Olustur</>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="send-test-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg"><Send className="h-5 w-5 text-blue-500" />Test Mesaji Gonder</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendTest} className="space-y-3">
              <div><Label className="text-xs">Telefon</Label><Input placeholder="905xxxxxxxxx" value={sendForm.phone} onChange={e => setSendForm({...sendForm, phone: e.target.value})} data-testid="test-phone-input" /></div>
              <div><Label className="text-xs">Mesaj</Label><Textarea placeholder="Mesaj..." value={sendForm.message} onChange={e => setSendForm({...sendForm, message: e.target.value})} rows={3} data-testid="test-message-input" /></div>
              <Button type="submit" disabled={isSending || !sendForm.phone || !sendForm.message} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="send-test-btn">
                {isSending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Gonderiliyor...</> : <><Send className="mr-2 h-4 w-4" />Gonder</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Broadcast */}
      <Card data-testid="broadcast-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg"><Megaphone className="h-5 w-5 text-red-500" />Bayilere Duyuru</CardTitle>
          <CardDescription>Toplu mesaj gonderin - il bazli filtreleme ve dosya gonderimi destekli</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Target Mode Selector */}
          <div>
            <Label className="text-xs text-gray-500 mb-2 block">Hedef Kitle</Label>
            <div className="flex gap-2 flex-wrap">
              {[
                { key: 'all', label: 'Tum Bayiler', icon: Users, count: totalDealers },
                { key: 'city', label: 'Il Secimi', icon: MapPin, count: selectedCities.length > 0 ? allDealers.filter(d => selectedCities.includes(d.city || 'Belirtilmemis')).length : '-' },
                { key: 'custom', label: 'Bayi Secimi', icon: Filter, count: selectedDealerIds.length || '-' },
              ].map(({ key, label, icon: Icon, count }) => (
                <button
                  key={key}
                  onClick={() => { setBroadcastMode(key); setSelectedCities([]); setSelectedDealerIds([]); }}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all',
                    broadcastMode === key
                      ? 'bg-red-50 border-red-300 text-red-700 shadow-sm'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                  )}
                  data-testid={`broadcast-mode-${key}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{label}</span>
                  <Badge variant="outline" className="text-[10px] px-1.5">{count}</Badge>
                </button>
              ))}
            </div>
          </div>

          {/* City Selection Panel */}
          {broadcastMode === 'city' && (
            <div className="border rounded-lg p-3 bg-gray-50/50 space-y-3" data-testid="city-selection-panel">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium text-gray-700">Il Secimi</span>
                {selectedCities.length > 0 && (
                  <Badge className="bg-red-100 text-red-700 text-[10px]">{selectedCities.length} il secildi</Badge>
                )}
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <Input
                  placeholder="Il ara..."
                  value={citySearch}
                  onChange={e => setCitySearch(e.target.value)}
                  className="pl-8 h-9 text-sm"
                  data-testid="city-search-input"
                />
              </div>
              <div className="max-h-[200px] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                {filteredCities.map(city => (
                  <button
                    key={city.name}
                    onClick={() => toggleCity(city.name)}
                    className={cn(
                      'flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs font-medium transition-all border',
                      selectedCities.includes(city.name)
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    )}
                    data-testid={`city-btn-${city.name}`}
                  >
                    <span className="truncate">{city.name}</span>
                    <span className="text-[10px] ml-1 opacity-60">({city.count})</span>
                  </button>
                ))}
                {filteredCities.length === 0 && <p className="col-span-full text-center text-xs text-gray-400 py-2">Il bulunamadi</p>}
              </div>
              {selectedCities.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {selectedCities.map(city => (
                    <Badge key={city} variant="outline" className="text-[10px] px-2 py-0.5 bg-red-50 border-red-200 text-red-700 cursor-pointer hover:bg-red-100" onClick={() => toggleCity(city)}>
                      {city} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                  <button onClick={() => setSelectedCities([])} className="text-[10px] text-gray-400 hover:text-red-500 ml-1">Temizle</button>
                </div>
              )}
            </div>
          )}

          {/* Custom Dealer Selection */}
          {broadcastMode === 'custom' && (
            <div className="border rounded-lg p-3 bg-gray-50/50 space-y-3" data-testid="dealer-selection-panel">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium text-gray-700">Bayi Secimi</span>
                  {selectedDealerIds.length > 0 && (
                    <Badge className="bg-red-100 text-red-700 text-[10px]">{selectedDealerIds.length} bayi secildi</Badge>
                  )}
                </div>
                <button
                  onClick={selectAllFilteredDealers}
                  className="text-[10px] text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="select-all-dealers-btn"
                >
                  {filteredDealers.every(d => selectedDealerIds.includes(d.id)) ? 'Hepsini Kaldir' : 'Hepsini Sec'}
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <Input
                  placeholder="Bayi ara (isim, firma, il, telefon)..."
                  value={dealerSearch}
                  onChange={e => setDealerSearch(e.target.value)}
                  className="pl-8 h-9 text-sm"
                  data-testid="dealer-search-input"
                />
              </div>
              <div className="max-h-[250px] overflow-y-auto space-y-1">
                {filteredDealers.map(dealer => (
                  <label
                    key={dealer.id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all border',
                      selectedDealerIds.includes(dealer.id)
                        ? 'bg-red-50 border-red-200'
                        : 'bg-white border-transparent hover:bg-gray-50'
                    )}
                    data-testid={`dealer-checkbox-${dealer.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDealerIds.includes(dealer.id)}
                      onChange={() => toggleDealer(dealer.id)}
                      className="rounded border-gray-300 text-[#1a224f] focus:ring-[#1a224f] h-4 w-4"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{dealer.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        {dealer.company_name && <span>{dealer.company_name}</span>}
                        {dealer.city && <><span>-</span><span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{dealer.city}</span></>}
                        {dealer.phone && <span>{dealer.phone}</span>}
                      </div>
                    </div>
                    {!dealer.phone && <Badge variant="outline" className="text-[9px] px-1 text-amber-600 border-amber-200 shrink-0">Tel yok</Badge>}
                  </label>
                ))}
                {filteredDealers.length === 0 && <p className="text-center text-xs text-gray-400 py-4">Bayi bulunamadi</p>}
              </div>
            </div>
          )}

          {/* Message Input */}
          <div>
            <Label className="text-xs text-gray-500 mb-1.5 block">Duyuru Mesaji</Label>
            <Textarea
              placeholder="Duyuru mesajinizi yazin..."
              value={broadcastMsg}
              onChange={(e) => setBroadcastMsg(e.target.value)}
              rows={4}
              data-testid="broadcast-message-input"
            />
          </div>

          {/* File Upload */}
          <div>
            <Label className="text-xs text-gray-500 mb-1.5 block">Dosya Ekle (Opsiyonel)</Label>
            {uploadedFile ? (
              <div className="flex items-center gap-3 p-3 rounded-lg border border-green-200 bg-green-50/50" data-testid="uploaded-file-info">
                <Paperclip className="h-4 w-4 text-green-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{uploadedFile.filename}</p>
                  <p className="text-[10px] text-gray-400">{formatFileSize(uploadedFile.size)}</p>
                </div>
                <button onClick={removeFile} className="p-1 text-gray-400 hover:text-red-500 hover:bg-[#eef0f7] rounded" data-testid="remove-file-btn">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-red-300 hover:bg-[#eef0f7]/30 transition-all" data-testid="file-upload-area">
                <input type="file" className="hidden" onChange={handleFileUpload} disabled={isUploading} accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.mp3,.zip,.rar" />
                {isUploading ? (
                  <><Loader2 className="h-5 w-5 animate-spin text-gray-400" /><span className="text-sm text-gray-500">Yukleniyor...</span></>
                ) : (
                  <><Upload className="h-5 w-5 text-gray-400" /><span className="text-sm text-gray-500">Dosya yuklemek icin tiklayin (Maks 16MB)</span></>
                )}
              </label>
            )}
          </div>

          {/* Send Action */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="text-xs text-gray-500">
              <span className="font-semibold text-gray-700">{targetCount}</span> bayiye gonderilecek
              {uploadedFile && <span className="ml-2 text-green-600 font-medium">+ dosya</span>}
            </div>
            <Button
              onClick={handleBroadcast}
              disabled={isBroadcasting || !broadcastMsg.trim() || targetCount === 0}
              className="bg-[#1a224f] hover:bg-[#12173a]"
              data-testid="broadcast-send-btn"
            >
              {isBroadcasting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Gonderiliyor...</> : <><Megaphone className="mr-2 h-4 w-4" />Duyuru Gonder</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Message Templates */}
      <Card data-testid="templates-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg"><FileText className="h-5 w-5 text-red-500" />Mesaj Sablonlari</CardTitle>
          <CardDescription>Odeme sozu ve vade hatirlatma mesaj sablonlarini duzenleyin</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {filteredTemplates.length > 0 ? filteredTemplates.map((tmpl) => {
            const isExpanded = expandedTemplate === tmpl.template_type;
            return (
              <div key={tmpl.template_type} className="rounded-lg border transition-all" data-testid={`template-${tmpl.template_type}`}>
                <button className="flex w-full items-center justify-between p-3 sm:p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => handleExpandTemplate(tmpl.template_type)} data-testid={`template-btn-${tmpl.template_type}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">{tmpl.name}</span>
                    {tmpl.is_default ? <Badge variant="outline" className="text-[10px] px-1.5">Varsayilan</Badge> : <Badge className="text-[10px] px-1.5 bg-red-50 text-[#1a224f] border-red-200">Ozel</Badge>}
                  </div>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </button>
                {isExpanded && (
                  <div className="border-t p-3 sm:p-4 space-y-3">
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">Degiskenler: {tmpl.variables?.map(v => `{${v}}`).join(', ')}</Label>
                      <Textarea value={editingContent} onChange={(e) => setEditingContent(e.target.value)} rows={5} className="font-mono text-sm" data-testid={`editor-${tmpl.template_type}`} />
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      {!tmpl.is_default && (
                        <Button variant="outline" size="sm" onClick={() => handleResetTemplate(tmpl.template_type)} disabled={savingTemplate === tmpl.template_type} data-testid={`reset-${tmpl.template_type}`}>
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Varsayilana Don
                        </Button>
                      )}
                      <Button size="sm" onClick={() => handleSaveTemplate(tmpl.template_type)} disabled={savingTemplate === tmpl.template_type || !editingContent.trim()} data-testid={`save-${tmpl.template_type}`}>
                        {savingTemplate === tmpl.template_type ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="mr-1.5 h-3.5 w-3.5" />}Kaydet
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          }) : (
            <div className="text-center py-6 text-gray-500 text-sm">
              <FileText className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p>Mesaj sablonu bulunamadi.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Queue */}
      <Card data-testid="message-queue-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg"><Inbox className="h-5 w-5 text-red-500" />Mesaj Kuyrugu</CardTitle>
              <CardDescription>Gonderilen ve bekleyen mesajlar</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { fetchAll(); if (msgTab === 'deleted') fetchDeleted(); }} data-testid="refresh-messages-btn"><RefreshCw className="mr-1.5 h-4 w-4" />Yenile</Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <button onClick={() => setMsgTab('active')} className={cn('px-3 py-1.5 rounded-lg text-sm font-medium transition-all', msgTab === 'active' ? 'bg-red-50 text-red-700 border border-red-200' : 'text-gray-500 hover:bg-gray-50')} data-testid="msg-tab-active">
              Mesajlar ({msgStats.total_messages || 0})
            </button>
            <button onClick={() => { setMsgTab('deleted'); fetchDeleted(); }} className={cn('px-3 py-1.5 rounded-lg text-sm font-medium transition-all', msgTab === 'deleted' ? 'bg-red-50 text-red-700 border border-red-200' : 'text-gray-500 hover:bg-gray-50')} data-testid="msg-tab-deleted">
              <Archive className="w-3.5 h-3.5 inline mr-1" />Silinenler ({deletedMessages.length})
            </button>
          </div>

          {/* Stats - tıklanabilir filtreler */}
          <div className="grid gap-2 grid-cols-2 lg:grid-cols-4 mb-4">
            {[
              { key: 'pending', icon: Clock, color: 'text-amber-500', activeColor: 'border-amber-300 bg-amber-50', label: 'Kuyrukta', val: msgStats.pending_in_queue || 0 },
              { key: 'sent', icon: CheckCircle, color: 'text-green-500', activeColor: 'border-green-300 bg-green-50', label: 'Gonderilen', val: `${msgStats.total_sent || 0} (${msgStats.today_sent || 0})` },
              { key: 'failed', icon: XCircle, color: 'text-red-500', activeColor: 'border-red-300 bg-red-50', label: 'Hatali', val: `${msgStats.total_failed || 0} (${msgStats.today_failed || 0})` },
              { key: 'all', icon: MessageCircle, color: 'text-blue-500', activeColor: 'border-blue-300 bg-blue-50', label: 'Toplam', val: msgStats.total_messages || 0 },
            ].map(({ key, icon: I, color, activeColor, label, val }) => (
              <button
                key={key}
                onClick={() => setMsgFilter(prev => prev === key ? 'all' : key)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border p-3 transition-all text-left hover:shadow-sm',
                  msgFilter === key ? activeColor + ' shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50'
                )}
                data-testid={`msg-stat-${key}`}
              >
                <I className={cn('h-4 w-4 shrink-0', color)} />
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-500 truncate">{label}</p>
                  <p className="font-bold text-sm">{val}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Aktif filtre göstergesi */}
          {msgFilter !== 'all' && (
            <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-blue-50/50 border border-blue-200/60 rounded-lg">
              <Filter className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs text-blue-700 font-medium">
                Filtre: {msgFilter === 'pending' ? 'Kuyruktakiler' : msgFilter === 'sent' ? 'Gönderilenler' : msgFilter === 'failed' ? 'Hatalılar' : 'Tümü'}
                {' '}({messages.length} gösteriliyor)
              </span>
              <button onClick={() => setMsgFilter('all')} className="ml-auto text-[10px] text-blue-600 hover:text-blue-800 font-medium" data-testid="clear-msg-filter">
                Temizle
              </button>
            </div>
          )}

          {/* Active Messages */}
          {msgTab === 'active' && (
            <>
            {failedCount > 0 && (
              <div className="flex items-center justify-between mb-3 p-3 bg-red-50/50 border border-red-200/60 rounded-lg">
                <span className="text-sm text-red-700 font-medium">{failedCount} hatalı mesaj var</span>
                <button onClick={handleRetryAllFailed} disabled={retryingAll} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1a224f] text-white text-xs font-medium rounded-lg hover:bg-[#12173a] disabled:opacity-50 transition-colors" data-testid="retry-all-failed-btn">
                  {retryingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                  Tumunu Yeniden Gonder
                </button>
              </div>
            )}
            {messages.length === 0 ? (
              <div className="py-8 text-center text-gray-500"><Inbox className="mx-auto mb-2 h-8 w-8 opacity-30" /><p className="text-sm">Henuz mesaj yok</p></div>
            ) : filteredMessages.length === 0 ? (
              <div className="py-8 text-center text-gray-500"><Filter className="mx-auto mb-2 h-8 w-8 opacity-30" /><p className="text-sm">Bu filtreye uygun mesaj yok</p></div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block bg-white border border-gray-200 rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                  <table className="w-full text-[13px]">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="w-10 px-2 py-2.5 text-center text-xs font-semibold text-gray-500">#</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Telefon</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Mesaj</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 w-32">Tip</th>
                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-28">Durum</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 w-36">Tarih</th>
                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-24">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMessages.map((msg, idx) => (
                        <tr key={msg.id} className={`border-b border-gray-100 transition-colors ${idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`} data-testid={`msg-${msg.id}`}>
                          <td className="px-2 py-2 text-center text-gray-400 text-xs">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{msg.phone}</td>
                          <td className="px-3 py-2 text-gray-600">
                            <p className="line-clamp-2 max-w-[400px]">{msg.message_content}</p>
                            {msg.error_message && <p className="mt-0.5 text-[11px] text-[#1a224f] flex items-center gap-1"><AlertTriangle className="h-3 w-3 shrink-0" />{msg.error_message}</p>}
                          </td>
                          <td className="px-3 py-2 text-gray-500 text-[12px]">{msgTypeLabel(msg.message_type)}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium',
                              msg.status === 'sent' && 'bg-green-100 text-green-700',
                              msg.status === 'failed' && 'bg-red-100 text-red-700',
                              msg.status === 'pending' && 'bg-amber-100 text-amber-700')}>
                              {msg.status === 'sent' ? 'Gönderildi' : msg.status === 'failed' ? 'Hata' : 'Beklemede'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-400 text-[11px] whitespace-nowrap">{formatDate(msg.created_at)}</td>
                          <td className="px-3 py-2 text-center">
                            <div className="inline-flex items-center gap-0.5">
                              {msg.status === 'failed' && (
                                <button onClick={() => handleResendMessage(msg.id)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Yeniden Gönder" data-testid={`resend-${msg.id}`}>
                                  <RotateCw className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button onClick={() => handleDeleteMessage(msg.id)} className="p-1.5 text-red-400 hover:bg-[#eef0f7] rounded" title="Sil" data-testid={`delete-msg-${msg.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile Cards */}
                <div className="lg:hidden max-h-[400px] overflow-y-auto space-y-2 pr-1">
                  {filteredMessages.map((msg) => (
                    <div key={msg.id} className={cn('rounded-lg border p-3', msg.status === 'sent' && 'bg-green-50/50 border-green-200/60', msg.status === 'failed' && 'bg-red-50/50 border-red-200/60', msg.status === 'pending' && 'bg-amber-50/50 border-amber-200/60')}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">{msg.phone}</span>
                            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', msg.status === 'sent' && 'border-green-300 text-green-700 bg-green-100', msg.status === 'failed' && 'border-red-300 text-red-700 bg-red-100', msg.status === 'pending' && 'border-amber-300 text-amber-700 bg-amber-100')}>
                              {msg.status === 'sent' ? 'Gonderildi' : msg.status === 'failed' ? 'Hata' : 'Beklemede'}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{msgTypeLabel(msg.message_type)}</Badge>
                          </div>
                          <p className="mt-1 text-xs text-gray-500 line-clamp-2">{msg.message_content}</p>
                          {msg.error_message && <p className="mt-1 text-xs text-[#1a224f] flex items-center gap-1"><AlertTriangle className="h-3 w-3 shrink-0" />{msg.error_message}</p>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-[10px] text-gray-400 whitespace-nowrap mr-1">{formatDate(msg.created_at)}</span>
                          {msg.status === 'failed' && (
                            <button onClick={() => handleResendMessage(msg.id)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-md"><RotateCw className="w-3.5 h-3.5" /></button>
                          )}
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-1.5 text-red-400 hover:bg-[#eef0f7] rounded-md"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            </>
          )}

          {/* Deleted Messages */}
          {msgTab === 'deleted' && (
            deletedMessages.length === 0 ? (
              <div className="py-8 text-center text-gray-500"><Archive className="mx-auto mb-2 h-8 w-8 opacity-30" /><p className="text-sm">Silinen mesaj yok</p></div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
                {deletedMessages.map((msg) => (
                  <div key={msg.id} className="rounded-lg border p-3 bg-gray-50/50 border-gray-200/60" data-testid={`deleted-msg-${msg.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-gray-500">{msg.phone}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-gray-300 text-gray-500">Silindi</Badge>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{msgTypeLabel(msg.message_type)}</Badge>
                        </div>
                        <p className="mt-1 text-xs text-gray-400 line-clamp-2">{msg.message_content}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[10px] text-gray-400 whitespace-nowrap mr-1">{formatDate(msg.deleted_at)}</span>
                        <button onClick={() => handleRestoreMessage(msg.id)} className="p-1.5 text-green-500 hover:bg-green-50 rounded-md" title="Geri Yukle" data-testid={`restore-${msg.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
